import React, { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import './InventoryPanel.css';
import { getRarityColor, getRarityClass, getRarityLabel, getRarityGlow, inferRarity, RARITY_TIERS } from '../utils/rarityUtils';
import { formatCurrency } from './shared/formatCompact';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

// Helper: normalize magic from object {0:{...},1:{...}} to array [{...},{...}]
const normalizeMagic = (magic) => {
  if (!magic) return [];
  if (Array.isArray(magic)) return magic;
  if (typeof magic === 'object') return Object.values(magic);
  return [];
};

// ============================================================
// CONSTANTS
// ============================================================
const CATEGORIES = [
  { key: 'all',        label: 'All',         icon: '📦' },
  { key: 'equipment',  label: 'Equipment',   icon: '⚔️' },
  { key: 'consumable', label: 'Consumables', icon: '🧪' },
  { key: 'tome',       label: 'Tomes',       icon: '📖' },
  { key: 'pouch',      label: 'Pouch',       icon: '🪡' },
  { key: 'misc',       label: 'Misc',        icon: '🎒' },
  { key: 'spells',     label: 'Spells',      icon: '🔮' },
];

// Spell type display metadata
const SPELL_TYPE_META = {
  damage:       { label: 'Damage',      color: '#e74c3c', icon: '✨' },
  heal:         { label: 'Heal',        color: '#27ae60', icon: '✨' },
  buff_att:     { label: 'Buff ATT',    color: '#f39c12', icon: '✨' },
  buff_def:     { label: 'Buff DEF',    color: '#3498db', icon: '✨' },
  buff_stat:    { label: 'Buff Stat',   color: '#9b59b6', icon: '✨' },
  debuff_def:   { label: 'Debuff DEF',  color: '#8e44ad', icon: '✨' },
  debuff_att:   { label: 'Debuff ATT',  color: '#c0392b', icon: '✨' },
  debuff_miss:  { label: 'Miss %',      color: '#7f8c8d', icon: '✨' },
  hp_divide:    { label: 'HP Divide',   color: '#2c3e50', icon: '✨' },
  mirror_image: { label: 'Mirror',      color: '#1abc9c', icon: '✨' },
  divination:   { label: 'Divination',  color: '#f1c40f', icon: '✨' },
  travel:       { label: 'Travel',      color: '#16a085', icon: '✨' },
  combo_damage: { label: 'Combo',       color: '#e67e22', icon: '✨' },
  drain:        { label: 'Drain',       color: '#8e44ad', icon: '✨' },
  combo_buff:   { label: 'Combo Buff',  color: '#2ecc71', icon: '✨' },
  combo_debuff: { label: 'Combo Debuff',color: '#e74c3c', icon: '✨' },
  stat_up:      { label: 'Stat Up',     color: '#3498db', icon: '✨' },
  stat_down:    { label: 'Stat Down',   color: '#e74c3c', icon: '✨' },
};

const EQUIPPABLE_TYPES = ['weapon', 'breastplate', 'helm', 'gloves', 'grieves', 'pauldrons', 'belt', 'armor', 'backpack', 'ring', 'shield'];
const USABLE_TYPES = ['restore', 'scroll', 'recipe_scroll', 'enc'];

// Enchantment scrolls must be applied at a crafting station — not usable from inventory
const isEnchantScroll = (item) =>
  item.type === 'scroll' && item.spell && item.spell.startsWith('enchant_');

const SORT_OPTIONS = [
  { key: 'name',  label: 'Name' },
  { key: 'type',  label: 'Type' },
  { key: 'value', label: 'Value' },
  { key: 'qty',   label: 'Quantity' },
];

// Map item type to display category
function itemCategory(type) {
  if (EQUIPPABLE_TYPES.includes(type)) return 'equipment';
  if (USABLE_TYPES.includes(type)) return 'consumable';
  if (type === 'tome') return 'tome';
  if (type === 'parts' || type === 'gem') return 'parts';
  return 'misc';
}

// Color coding for item types
const TYPE_COLORS = {
  weapon:      '#e74c3c',
  breastplate: '#3498db',
  helm:        '#3498db',
  gloves:      '#3498db',
  grieves:     '#3498db',
  pauldrons:   '#3498db',
  belt:        '#3498db',
  armor:       '#3498db',
  ring:        '#e991e8',
  shield:      '#3498db',
  restore:     '#2ecc71',
  scroll:      '#9b59b6',
  tome:        '#f39c12',
  parts:       '#95a5a6',
  gem:         '#e91e63',
  misc:        '#777',
  inn:         '#888',
  mount:       '#27ae60',
  backpack:    '#d4a017',
};

// Get display slot name
function getEquipSlotLabel(item) {
  if (item.type === 'weapon') return 'Weapon';
  if (item.type === 'helm') return 'Helm';
  if (item.type === 'breastplate' || item.type === 'armor') return 'Breastplate';
  if (item.type === 'belt') return 'Belt';
  if (item.type === 'backpack') return 'Backpack';
  if (item.type === 'ring') return 'Ring';
  if (item.type === 'shield') return 'Shield';
  if (['gloves', 'grieves', 'pauldrons'].includes(item.type)) {
    const side = item.slot === 'right' || item.slot?.includes('right') ? 'Right' : 'Left';
    const piece = item.type === 'gloves' ? 'Glove' : item.type === 'grieves' ? 'Grieve' : 'Pauldron';
    return `${side} ${piece}`;
  }
  return item.type;
}

function getEquipSlot(item) {
  if (['gloves', 'grieves', 'pauldrons'].includes(item.type)) {
    const side = (item.slot && item.slot.includes('right')) ? 'right' : 'left';
    return `${item.type}_${side}`;
  }
  if (item.type === 'armor') return 'breastplate';
  if (item.type === 'ring') return 'ring_left';
  if (item.type === 'shield') return 'offhand';
  return item.type;
}

// ============================================================
// ITEM TOOLTIP COMPONENT
// ============================================================
const ItemTooltip = React.forwardRef(({ item, equippedItem, canLearnSpells, style, onClose }, ref) => {
  const rarityColor = getRarityColor(item);
  const rarityLabel = getRarityLabel(item);

  // Compute comparison deltas against currently equipped item
  const deltas = [];
  if (equippedItem && equippedItem.name !== item.name) {
    const compare = (newVal, oldVal, label) => {
      const nv = parseInt(newVal) || 0;
      const ov = parseInt(oldVal) || 0;
      if (nv === 0 && ov === 0) return;
      const diff = nv - ov;
      if (diff !== 0) {
        deltas.push({
          label,
          diff: diff > 0 ? `+${diff}` : `${diff}`,
          color: diff > 0 ? '#4caf50' : '#f44336',
          icon: diff > 0 ? '▲' : '▼'
        });
      }
    };
    compare(item.att, equippedItem.att, 'Attack');
    compare(item.bonus, equippedItem.bonus, 'Defense');
    compare(item.dr, equippedItem.dr, 'Dmg Reduction');
    compare(item.size, equippedItem.size, 'Capacity');
    // Compare magic totals
    const magicSum = (raw) => {
      const sums = {};
      normalizeMagic(raw).forEach(m => { sums[m.type] = (sums[m.type] || 0) + (m.mod || m.bonus || 0); });
      return sums;
    };
    const newMagic = magicSum(item.magic);
    const oldMagic = magicSum(equippedItem.magic);
    const allTypes = new Set([...Object.keys(newMagic), ...Object.keys(oldMagic)]);
    allTypes.forEach(t => {
      const diff = (newMagic[t] || 0) - (oldMagic[t] || 0);
      if (diff !== 0) {
        deltas.push({
          label: t.toUpperCase(),
          diff: diff > 0 ? `+${diff}` : `${diff}`,
          color: diff > 0 ? '#4caf50' : '#f44336',
          icon: diff > 0 ? '▲' : '▼'
        });
      }
    });
  }

  return (
    <div className="inv-tooltip" style={style} ref={ref}>
      {onClose && <button className="inv-tooltip-close" onClick={onClose} aria-label="Close">✖</button>}
      <div className={`inv-tooltip-name ${getRarityClass(item)}`} style={{ color: rarityColor, textShadow: getRarityGlow(item) }}>{item.name}</div>
      <div className="inv-tooltip-rarity" style={{ color: rarityColor }}>{rarityLabel}</div>
      <div className="inv-tooltip-type">{(item.type || 'misc').toUpperCase()}</div>
      {!EQUIPPABLE_TYPES.includes(item.type) && item.description && item.description !== 'Unknown item' && <div className="inv-tooltip-desc">{item.description}</div>}
      {item.lore && <div className="inv-tooltip-lore">{item.lore}</div>}
      <div className="inv-tooltip-divider" />

      {/* Quality & Enhancement */}
      {item.quality && item.quality !== 'standard' && (
        <div className="inv-tooltip-stat" style={{ color: '#d4a017' }}>Quality: {item.quality}</div>
      )}
      {item.enhancement > 0 && (
        <div className="inv-tooltip-stat" style={{ color: '#4caf50' }}>Enhanced: +{item.enhancement}</div>
      )}

      {/* Equipment stats */}
      {item.damage && <div className="inv-tooltip-stat">Damage: {item.damage}</div>}
      {item.att && <div className="inv-tooltip-stat">Attack: +{item.att}</div>}
      {item.bonus && <div className="inv-tooltip-stat">Defense: +{item.bonus}</div>}
      {item.dr && <div className="inv-tooltip-stat">Dmg Reduction: {item.dr}</div>}
      {item.mtype && <div className="inv-tooltip-stat">Melee Type: {item.mtype}</div>}
      {item.size && <div className="inv-tooltip-stat" style={{ color: '#d4a017' }}>Capacity: +{item.size} slots</div>}

      {/* Elemental damage */}
      {item.mdamage && item.mtype && (
        <div className="inv-tooltip-stat" style={{ color: '#ff9800' }}>{item.mtype} damage: {item.mdamage}</div>
      )}

      {/* Sockets */}
      {item.sockets && item.sockets.length > 0 && (
        <div className="inv-tooltip-stat" style={{ color: '#e91e63' }}>
          Gems: {item.sockets.filter(s => s).length}/{item.sockets.length} socketed
        </div>
      )}

      {/* Enchantments */}
      {item.enchantments && item.enchantments.length > 0 && (
        <div className="inv-tooltip-magic">
          {item.enchantments.map((ench, i) => (
            <div key={i} className="inv-tooltip-stat" style={{ color: '#ffb74d' }}>
              📜 {ench.label}
            </div>
          ))}
        </div>
      )}

      {/* Consumable stats */}
      {item.hp > 0 && <div className="inv-tooltip-stat" style={{ color: '#e74c3c' }}>Restores {item.hp} HP</div>}
      {item.mp > 0 && <div className="inv-tooltip-stat" style={{ color: '#3498db' }}>Restores {item.mp} MP</div>}

      {/* Tome info */}
      {item.spell && <div className="inv-tooltip-stat" style={{ color: '#f39c12' }}>Teaches: {item.spell}</div>}
      {item.type === 'tome' && canLearnSpells === false && (
        <div className="inv-tooltip-stat" style={{ color: '#e74c3c', fontStyle: 'italic' }}>⚠ Your class cannot learn magic</div>
      )}

      {/* Magic bonuses */}
      {item.magic && normalizeMagic(item.magic).filter(m => (m.mod || m.bonus || 0) !== 0).length > 0 && (
        <div className="inv-tooltip-magic">
          {normalizeMagic(item.magic).filter(m => (m.mod || m.bonus || 0) !== 0).map((m, i) => (
            <div key={i} className="inv-tooltip-stat" style={{ color: '#bf7fff' }}>
               +{m.mod || m.bonus || 0} {m.type === 'ablup' ? (m.abl || '').toUpperCase() : m.type.toUpperCase()}
            </div>
          ))}
        </div>
      )}

      {/* Level requirement */}
      {item.level > 1 && <div className="inv-tooltip-level">Requires Lv {item.level}</div>}

      {/* Shield class restriction */}
      {item.type === 'shield' && (
        <div className="inv-tooltip-stat" style={{ color: '#ff9800', fontStyle: 'italic' }}>⚠ Non-Fighter classes: -3 ATT penalty</div>
      )}

      {/* Value */}
      <div className="inv-tooltip-value">{item.cost || 0} gp (sell: {Math.floor((item.cost || 0) / 4)} gp)</div>

      {(item.quantity || 1) > 1 && (
        <div className="inv-tooltip-qty">Quantity: {item.quantity}</div>
      )}

      {/* Stat comparison vs equipped */}
      {deltas.length > 0 && (
        <div className="inv-tooltip-compare">
          <div className="inv-tooltip-divider" />
          <div className="inv-tooltip-compare-header">vs {equippedItem.name}</div>
          {deltas.map((d, i) => (
            <div key={i} className="inv-tooltip-delta" style={{ color: d.color }}>
              {d.icon} {d.label}: {d.diff}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

// ============================================================
// MAIN INVENTORY PANEL COMPONENT
// Classes that can learn spells from tomes (have seed-based magic)
const CASTER_CLASSES = ['Mage', 'Cleric', 'Psion', 'Druid'];

// ============================================================
const InventoryPanel = ({ character, onClose, onCharacterUpdate, onLogMessage, onTeleport }) => {
  const canLearnSpells = CASTER_CLASSES.includes(character?.class);
  const [category, setCategory] = useState('all');
  const [sortBy, setSortBy] = useState('type');
  const [searchText, setSearchText] = useState('');
  const [rarityFilter, setRarityFilter] = useState('all'); // v639.9
  const [busyAction, setBusyAction] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [tooltipItem, setTooltipItem] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [tooltipEquipped, setTooltipEquipped] = useState(null); // equipped item for comparison
  const [confirmDrop, setConfirmDrop] = useState(null); // { item, qty }
  const [dropQty, setDropQty] = useState(1);
  const [allSpells, setAllSpells] = useState(null); // lazy-loaded from server
  const [equippedSpellNames, setEquippedSpellNames] = useState([]);
  const [savedSpellNames, setSavedSpellNames] = useState([]); // last-saved snapshot for dirty detection
  const [unsavedPrompt, setUnsavedPrompt] = useState(null); // { onProceed } when blocking navigation
  const [spellTypeFilter, setSpellTypeFilter] = useState('all');
  const [spellSearch, setSpellSearch] = useState('');
  const [spellsLoading, setSpellsLoading] = useState(false);
  const panelRef = useRef(null);
  const tooltipRef = useRef(null);

  // Detect touch device — on touch, we use tap-to-toggle instead of hover
  const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

  // After tooltip renders, adjust if it overflows the viewport
  useLayoutEffect(() => {
    const el = tooltipRef.current;
    if (!el || !tooltipItem) return;
    const rect = el.getBoundingClientRect();
    const viewportH = window.innerHeight;
    if (rect.bottom > viewportH - 8) {
      el.style.top = `${Math.max(8, viewportH - rect.height - 8)}px`;
    }
    if (rect.top < 8) {
      el.style.top = '8px';
    }
  });

  // Capture-phase Escape/I listener. GamePage has a global handler that
  // closes the inventory unconditionally; when the spell loadout has unsaved
  // edits we intercept the keystroke first so the confirmation prompt fires
  // instead of silently dropping the changes.
  // (Hook stays above the `if (!character) return null` early return so it
  // is called on every render — required by rules-of-hooks.)
  useEffect(() => {
    // Inline-compute dirty so the effect's dep array can be primitives.
    if (allSpells === null) return undefined;
    if (equippedSpellNames.length === savedSpellNames.length) {
      const a = [...equippedSpellNames].sort();
      const b = [...savedSpellNames].sort();
      let same = true;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) { same = false; break; }
      if (same) return undefined;
    }
    const onKey = (event) => {
      if (event.key === 'Escape' || (event.key && event.key.toLowerCase && event.key.toLowerCase() === 'i')) {
        const tag = (event.target && event.target.tagName) || '';
        // Don't hijack the "i" key while the user is typing in an input.
        if (event.key.toLowerCase && event.key.toLowerCase() === 'i' && (tag === 'INPUT' || tag === 'TEXTAREA')) return;
        event.stopPropagation();
        event.preventDefault();
        setUnsavedPrompt({ onProceed: () => onClose() });
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [allSpells, equippedSpellNames, savedSpellNames, onClose]);

  if (!character) return null;

  // Parse inventory & equipment
  const parseJSON = (val, fallback) => {
    if (!val) return fallback;
    if (typeof val !== 'string') return val;
    try { return JSON.parse(val); } catch { return fallback; }
  };

  const inventory = parseJSON(character.inventory, []).filter(i => i && typeof i === 'object');
  const pouch = parseJSON(character.pouch, []).filter(i => i && typeof i === 'object');
  const pouchSize = character.pouch_size || character.pouchSize || 100;
  const equipment = parseJSON(character.equipment, {});
  const acAbilities = parseJSON(character.ac_abilities, {});
  const hasDualWield = acAbilities.dual_wield >= 1;

  // Flash status message
  const flash = (msg) => {
    setStatusMsg(msg);
    setTimeout(() => setStatusMsg(''), 3000);
  };

  // ============================================================
  // API CALLS
  // ============================================================
  const apiCall = async (url, body) => {
    const res = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(body)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  };

  const handleEquip = async (itemName, slotOverride = null) => {
    if (busyAction) return;
    setBusyAction(itemName);
    try {
      const body = { itemName };
      if (slotOverride) body.slotOverride = slotOverride;
      const data = await apiCall('/api/player/equip', body);
      flash(data.message);
      if (onLogMessage) onLogMessage(data.message);
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (err) {
      flash(`${err.message}`);
    }
    setBusyAction(null);
  };

  const handleUnequip = async (slot) => {
    if (busyAction) return;
    setBusyAction(slot);
    try {
      const data = await apiCall('/api/player/unequip', { slot });
      flash(data.message);
      if (onLogMessage) onLogMessage(data.message);
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (err) {
      flash(`${err.message}`);
    }
    setBusyAction(null);
  };

  const handleUseItem = async (itemName) => {
    if (busyAction) return;
    setBusyAction(itemName);
    try {
      const data = await apiCall('/api/player/use-item', { itemName });
      flash(data.message);
      if (onLogMessage) onLogMessage(data.message);
      if (data.teleported && onTeleport) {
        onTeleport();
        return; // panel will close, skip further updates
      }
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (err) {
      flash(`${err.message}`);
    }
    setBusyAction(null);
  };

  const handleUseTome = async (itemName) => {
    if (busyAction) return;
    setBusyAction(itemName);
    try {
      const data = await apiCall('/api/player/use-tome', { itemName });
      flash(`${data.message}`);
      if (onLogMessage) onLogMessage(data.message);
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (err) {
      flash(`${err.message}`);
    }
    setBusyAction(null);
  };

  const handleDropItem = async (itemName, quantity) => {
    if (busyAction) return;
    setBusyAction(itemName);
    try {
      const data = await apiCall('/api/player/drop-item', { itemName, quantity });
      flash(data.message);
      if (onLogMessage) onLogMessage(data.message);
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (err) {
      flash(`${err.message}`);
    }
    setBusyAction(null);
    setConfirmDrop(null);
  };

  // v639: Toggle lock state on an inventory item. Locked items are
  // skipped by sell, salvage, drop, mail, and market list paths.
  const handleToggleLock = async (item) => {
    if (busyAction) return;
    const itemIndex = inventory.indexOf(item);
    if (itemIndex < 0) {
      flash('Cannot lock: item not found in inventory');
      return;
    }
    const wantLocked = !item.locked;
    setBusyAction(`lock-${itemIndex}`);
    try {
      const data = await apiCall('/api/player/lock-item', { itemIndex, locked: wantLocked });
      flash(data.locked ? `🔒 ${item.name} locked` : `🔓 ${item.name} unlocked`);
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (err) {
      flash(`${err.message}`);
    }
    setBusyAction(null);
  };

  // v639.4: Move every parts/gem stack from inventory into the materials
  // pouch in one click. Locked materials stay put.
  const handleDepositMaterials = async () => {
    if (busyAction) return;
    setBusyAction('deposit-mats');
    try {
      const data = await apiCall('/api/player/deposit-materials', {});
      flash(data.message || 'Deposited.');
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (err) {
      flash(`${err.message}`);
    }
    setBusyAction(null);
  };

  // v639.5: Consolidate fragmented stacks and sort inventory.
  const handleTidy = async () => {
    if (busyAction) return;
    setBusyAction('tidy');
    try {
      const data = await apiCall('/api/player/tidy-inventory', {});
      flash(data.message || 'Tidied.');
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (err) {
      flash(`${err.message}`);
    }
    setBusyAction(null);
  };

  // v639.7: Auto-equip the strongest eligible item into every empty slot.
  const handleEquipBest = async () => {
    if (busyAction) return;
    setBusyAction('equip-best');
    try {
      const data = await apiCall('/api/player/equip-best', {});
      flash(data.message || 'Equipped.');
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (err) {
      flash(`${err.message}`);
    }
    setBusyAction(null);
  };

  // ============================================================
  // SPELL LOADOUT
  // ============================================================
  const fetchSpells = async () => {
    if (allSpells !== null) return; // already loaded
    setSpellsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/player/available-spells`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (res.ok) {
        setAllSpells(data.spells || []);
        setEquippedSpellNames(data.equippedSpells || []);
        setSavedSpellNames(data.equippedSpells || []);
      }
    } catch (err) {
      flash('✨ Failed to load spells');
    }
    setSpellsLoading(false);
  };

  const handleToggleSpell = (spellName) => {
    setEquippedSpellNames(prev => {
      if (prev.includes(spellName)) return prev.filter(n => n !== spellName);
      return [...prev, spellName];
    });
  };

  const handleEquipAllVisible = (spellNames) => {
    setEquippedSpellNames(prev => {
      const combined = new Set([...prev, ...spellNames]);
      return [...combined];
    });
  };

  const handleUnequipAllVisible = (spellNames) => {
    setEquippedSpellNames(prev => prev.filter(n => !spellNames.includes(n)));
  };

  const handleClearAllSpells = () => {
    setEquippedSpellNames([]);
  };

  const handleSaveSpellLoadout = async () => {
    if (busyAction) return;
    setBusyAction('saving-spells');
    try {
      const data = await apiCall('/api/player/equipped-spells', { equippedSpells: equippedSpellNames });
      flash(`${data.message} (${equippedSpellNames.length} spells equipped)`);
      if (onLogMessage) onLogMessage(`Spell loadout updated: ${equippedSpellNames.length} spells equipped for combat`);
      if (onCharacterUpdate) onCharacterUpdate();
      setSavedSpellNames([...equippedSpellNames]);
    } catch (err) {
      flash(`${err.message}`);
    }
    setBusyAction(null);
  };

  // ============================================================
  // UNSAVED-LOADOUT GUARD
  // ============================================================
  // Compare equippedSpellNames against the last-saved snapshot. Returns true
  // when the player has pending edits in the spell-loadout view that haven't
  // been pushed to the server.
  const isLoadoutDirty = (() => {
    if (allSpells === null) return false; // never opened the loadout view
    if (equippedSpellNames.length !== savedSpellNames.length) return true;
    const a = [...equippedSpellNames].sort();
    const b = [...savedSpellNames].sort();
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return true;
    return false;
  })();

  // Funnel for every "navigate away" path (close button, overlay click, Esc/I,
  // category switch). If the loadout is dirty, prompts; otherwise runs the
  // proceed action immediately.
  const requestNavigateAway = (proceed) => {
    if (isLoadoutDirty) {
      setUnsavedPrompt({ onProceed: proceed });
    } else {
      proceed();
    }
  };

  const requestClose = () => {
    requestNavigateAway(() => onClose());
  };

  const handleConfirmSaveAndProceed = async () => {
    const proceed = unsavedPrompt?.onProceed;
    setUnsavedPrompt(null);
    await handleSaveSpellLoadout();
    if (proceed) proceed();
  };

  const handleConfirmDiscardAndProceed = () => {
    const proceed = unsavedPrompt?.onProceed;
    // Revert local state to the saved snapshot so re-opening doesn't show stale edits.
    setEquippedSpellNames([...savedSpellNames]);
    setUnsavedPrompt(null);
    if (proceed) proceed();
  };

  // ============================================================
  // TOOLTIP HANDLING
  // ============================================================
  const handleMouseEnter = (e, item) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const panelRect = panelRef.current?.getBoundingClientRect() || { left: 0, top: 0, width: 720 };
    // Position tooltip centered horizontally in the panel,
    // placed BELOW the hovered row (or above if not enough space below)
    const panelWidth = panelRect.width || 720;
    const tooltipWidth = 240; // approx avg between min (200) and max (280)
    const tooltipHeight = 250; // approx tooltip height
    const centeredX = (panelWidth - tooltipWidth) / 2;
    const gap = 6; // px gap between row and tooltip

    // Prefer below; if not enough space, show above
    let yPos;
    if (rect.bottom + gap + tooltipHeight < window.innerHeight) {
      yPos = rect.bottom + gap;
    } else {
      yPos = rect.top - tooltipHeight - gap;
    }

    setTooltipItem(item);
    setTooltipPos({
      x: Math.max(8, Math.min(panelRect.left + centeredX, window.innerWidth - tooltipWidth - 8)),
      y: yPos
    });
    // For equippable items, find what's in that slot for stat comparison
    if (EQUIPPABLE_TYPES.includes(item.type)) {
      const slot = getEquipSlot(item);
      setTooltipEquipped(equipment[slot] || null);
    } else {
      setTooltipEquipped(null);
    }
  };

  const handleMouseLeave = () => {
    if (isTouchDevice) return; // On touch, only close via close button or tapping same item
    setTooltipItem(null);
    setTooltipEquipped(null);
  };

  const handleItemTap = (e, item) => {
    // Toggle: tap same item to close, tap different item to switch
    if (tooltipItem && tooltipItem.name === item.name) {
      setTooltipItem(null);
      setTooltipEquipped(null);
      return;
    }
    handleMouseEnter(e, item);
  };

  // ============================================================
  // FILTERING & SORTING
  // ============================================================
  // v633: 'pouch' category sources from the materials pouch (separate storage),
  // not the main inventory. All other tabs filter the inventory array.
  let filteredItems = category === 'pouch' ? [...pouch] : [...inventory];

  // Category filter
  if (category !== 'all' && category !== 'pouch') {
    filteredItems = filteredItems.filter(item => itemCategory(item.type) === category);
  }

  // Search filter
  if (searchText.trim()) {
    const search = searchText.toLowerCase();
    filteredItems = filteredItems.filter(item =>
      (item.name || '').toLowerCase().includes(search) ||
      (item.description || '').toLowerCase().includes(search) ||
      (item.type || '').toLowerCase().includes(search)
    );
  }

  // v639.9: Rarity filter
  if (rarityFilter !== 'all') {
    filteredItems = filteredItems.filter(item => {
      const r = item.rarity || inferRarity(item);
      return r === rarityFilter;
    });
  }

  // Sort
  filteredItems.sort((a, b) => {
    switch (sortBy) {
      case 'name':  return (a.name || '').localeCompare(b.name || '');
      case 'type':  return (a.type || '').localeCompare(b.type || '') || (a.name || '').localeCompare(b.name || '');
      case 'value': return (b.cost || 0) - (a.cost || 0);
      case 'qty':   return (b.quantity || 1) - (a.quantity || 1);
      default:      return 0;
    }
  });

  // Count stats
  const totalValue = inventory.reduce((sum, i) => sum + (i.cost || 0) * (i.quantity || 1), 0);
  const equippedCount = Object.keys(equipment).length;

  // Inventory capacity (or pouch capacity when on pouch tab)
  const usedSlots = category === 'pouch' ? pouch.length : inventory.length;
  const capacity = category === 'pouch'
    ? pouchSize
    : 20 + (equipment.backpack && equipment.backpack.size ? equipment.backpack.size : 0);
  const capacityPct = capacity > 0 ? usedSlots / capacity : 0;
  const capacityColor = capacityPct >= 1 ? '#e74c3c' : capacityPct >= 0.8 ? '#f39c12' : '#2ecc71';

  // ============================================================
  // EQUIPPED GEAR SECTION
  // ============================================================
  const EQUIP_SLOTS = [
    { key: 'weapon', label: '⚔️ Weapon' },
    { key: 'offhand', label: '🗡️ Off-Hand' },
    { key: 'helm', label: '⛑️ Helm' },
    { key: 'breastplate', label: '✨ Chest' },
    { key: 'pauldrons_left', label: '✨ L.Pauldron' },
    { key: 'pauldrons_right', label: '✨ R.Pauldron' },
    { key: 'gloves_left', label: '✨ L.Glove' },
    { key: 'gloves_right', label: '✨ R.Glove' },
    { key: 'grieves_left', label: '✨ L.Grieve' },
    { key: 'grieves_right', label: '✨ R.Grieve' },
    { key: 'belt', label: '🪖 Belt' },
    { key: 'ring_left', label: '💍 Ring (L)' },
    { key: 'ring_right', label: '💍 Ring (R)' },
    { key: 'backpack', label: '🎒 Backpack' },
  ];

  const visibleEquipSlots = EQUIP_SLOTS.filter(s => s.key !== 'offhand' || hasDualWield || (equipment.offhand && equipment.offhand.type === 'shield')).map(s => {
    if (s.key === 'offhand' && equipment.offhand && equipment.offhand.type === 'shield') {
      return { ...s, label: '🛡️ Shield' };
    }
    return s;
  });

  return (
    <div className="inv-overlay" onClick={(e) => { if (e.target === e.currentTarget) requestClose(); }}>
      <div className="inv-panel" ref={panelRef}>
        {/* HEADER */}
        <div className="inv-header">
          <div className="inv-header-left">
            <h2>{category === 'pouch' ? 'Materials Pouch' : 'Inventory'}</h2>
            <span className="inv-header-stats">
              <span style={{ color: capacityColor, fontWeight: 'bold' }}>
                {usedSlots}/{capacity} slots
              </span>
              {'  '}
               {totalValue.toLocaleString()} gp value
            </span>
          </div>
          <button className="inv-close" onClick={requestClose} title="Close (I or Esc)">✖</button>
        </div>

        {/* STATUS MESSAGE */}
        {statusMsg && <div className="inv-status">{statusMsg}</div>}

        {/* EQUIPPED GEAR (compact) */}
        <div className="inv-equipped-section">
          <div className="inv-equipped-header" >
            <h3>Equipped Gear ({equippedCount}/10)</h3>
          </div>
          <div className="inv-equipped-grid">
            {visibleEquipSlots.map(({ key, label }) => {
              const item = equipment[key];
              return (
                <div
                  key={key}
                  className={`inv-equip-slot ${item ? 'filled' : ''}`}
                  onMouseEnter={!isTouchDevice && item ? (e) => handleMouseEnter(e, item) : undefined}
                  onMouseLeave={!isTouchDevice ? handleMouseLeave : undefined}
                  onClick={isTouchDevice && item ? (e) => handleItemTap(e, item) : undefined}
                >
                  <span className="inv-equip-label">{label}</span>
                  {item ? (
                    <div className="inv-equip-item">
                      <span className="inv-equip-name" style={{ color: getRarityColor(item), textShadow: getRarityGlow(item) }}>{item.name}</span>
                      <button
                        className="inv-btn-tiny danger"
                        onClick={() => handleUnequip(key)}
                        disabled={!!busyAction}
                        title="Unequip"
                      >✖</button>
                    </div>
                  ) : (
                    <span className="inv-equip-empty"></span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* FILTER BAR */}
        <div className="inv-filters">
          <div className="inv-category-tabs">
            {CATEGORIES.map(cat => (
              <button
                key={cat.key}
                className={`inv-cat-tab ${category === cat.key ? 'active' : ''}`}
                onClick={() => {
                  // Guard: leaving the spells tab with unsaved loadout edits
                  // should prompt the same as closing the panel.
                  if (category === 'spells' && cat.key !== 'spells') {
                    requestNavigateAway(() => {
                      setCategory(cat.key);
                      if (cat.key === 'spells') fetchSpells();
                    });
                    return;
                  }
                  setCategory(cat.key);
                  if (cat.key === 'spells') fetchSpells();
                }}
                title={cat.label}
              >
                <span className="inv-cat-icon">{cat.icon}</span>
                <span className="inv-cat-label">{cat.label}</span>
              </button>
            ))}
          </div>
          {category !== 'spells' && (
          <div className="inv-filter-row">
            <input
              className="inv-search"
              type="text"
              placeholder="🔍 Search items..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <select
              className="inv-sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {SORT_OPTIONS.map(opt => (
                <option key={opt.key} value={opt.key}>Sort: {opt.label}</option>
              ))}
            </select>
            <select
              className="inv-sort"
              value={rarityFilter}
              onChange={(e) => setRarityFilter(e.target.value)}
              title="Filter by rarity"
              style={{ marginLeft: 6, color: rarityFilter !== 'all' ? (RARITY_TIERS[rarityFilter]?.color || '#fff') : undefined }}
            >
              <option value="all">Rarity: All</option>
              {Object.keys(RARITY_TIERS).map(key => (
                <option key={key} value={key}>{RARITY_TIERS[key].label}</option>
              ))}
            </select>
            {category !== 'pouch' && filteredItems.some(it => it.type === 'parts' || it.type === 'gem') && (
              <button
                className="inv-btn"
                onClick={handleDepositMaterials}
                disabled={!!busyAction}
                title="Move all unlocked materials (parts/gems) from inventory to your pouch"
                style={{
                  marginLeft: 6, fontSize: '0.85em', whiteSpace: 'nowrap',
                  background: '#2a3a1a', color: '#a8d958', border: '1px solid #5a8a3a',
                  padding: '4px 10px', borderRadius: 4, cursor: 'pointer'
                }}
              >📦 Deposit Mats</button>
            )}
            <button
              className="inv-btn"
              onClick={handleTidy}
              disabled={!!busyAction}
              title="Merge fragmented stacks and sort by category + rarity"
              style={{
                marginLeft: 6, fontSize: '0.85em', whiteSpace: 'nowrap',
                background: '#2a2a3a', color: '#a8b8d9', border: '1px solid #5a6a8a',
                padding: '4px 10px', borderRadius: 4, cursor: 'pointer'
              }}
            >🧹 Tidy</button>
            {category !== 'pouch' && category !== 'spells' && (
              <button
                className="inv-btn"
                onClick={handleEquipBest}
                disabled={!!busyAction}
                title="Auto-equip the strongest eligible item from your inventory into every empty equipment slot. Already-equipped slots are not touched."
                style={{
                  marginLeft: 6, fontSize: '0.85em', whiteSpace: 'nowrap',
                  background: '#3a2a1a', color: '#d9b878', border: '1px solid #8a6a3a',
                  padding: '4px 10px', borderRadius: 4, cursor: 'pointer'
                }}
              >⚔️ Equip Best</button>
            )}
          </div>
          )}
        </div>

        {/* SPELL LOADOUT VIEW */}
        {category === 'spells' ? (
          <div className="inv-list">
            {spellsLoading ? (
              <div className="inv-empty">Loading spells...</div>
            ) : !allSpells || allSpells.length === 0 ? (
              <div className="inv-empty">
                <p>No spells available yet.</p>
                <p style={{ color: '#888', fontSize: '0.8em' }}>Learn seeds through combat or study tomes to unlock spells.</p>
              </div>
            ) : (() => {
              // Build type groups for filter tabs
              const spellTypes = {};
              allSpells.forEach(s => {
                const t = s.type || 'damage';
                if (!spellTypes[t]) spellTypes[t] = [];
                spellTypes[t].push(s);
              });
              const typeKeys = Object.keys(spellTypes);

              // Apply filters
              let visibleSpells = spellTypeFilter === 'all'
                ? [...allSpells]
                : allSpells.filter(s => (s.type || 'damage') === spellTypeFilter);
              if (spellSearch.trim()) {
                const q = spellSearch.toLowerCase();
                visibleSpells = visibleSpells.filter(s =>
                  s.name.toLowerCase().includes(q) ||
                  (s.element || '').toLowerCase().includes(q) ||
                  (s.type || '').toLowerCase().includes(q)
                );
              }
              // Sort: equipped first, then alphabetically
              visibleSpells.sort((a, b) => {
                const aEq = equippedSpellNames.includes(a.name) ? 0 : 1;
                const bEq = equippedSpellNames.includes(b.name) ? 0 : 1;
                if (aEq !== bEq) return aEq - bEq;
                return a.name.localeCompare(b.name);
              });

              const visibleNames = visibleSpells.map(s => s.name);
              const visibleEquipped = visibleSpells.filter(s => equippedSpellNames.includes(s.name)).length;

              return (
                <>
                  {/* Spell loadout header bar */}
                  <div className="spell-loadout-header">
                    <div className="spell-loadout-stats">
                      <span className="spell-loadout-count">
                         {equippedSpellNames.length} equipped
                        {equippedSpellNames.length === 0 && <span className="spell-hint"> (all available in combat)</span>}
                      </span>
                      <span className="spell-loadout-total">{allSpells.length} known</span>
                      {isLoadoutDirty && (
                        <span className="spell-loadout-dirty" title="You have unsaved changes">● Unsaved changes</span>
                      )}
                    </div>
                    <div className="spell-loadout-actions">
                      <button
                        className={`inv-btn spell-save ${isLoadoutDirty ? 'spell-save-pulse' : ''}`}
                        onClick={handleSaveSpellLoadout}
                        disabled={!!busyAction || !isLoadoutDirty}
                        title={isLoadoutDirty ? 'Save loadout' : 'No changes to save'}
                      >💾 Save Loadout</button>
                      <button
                        className="inv-btn cancel"
                        onClick={handleClearAllSpells}
                        disabled={!!busyAction}
                        title="Clear loadout (all spells available in combat)"
                      >Reset</button>
                    </div>
                  </div>

                  {/* Spell type filter tabs */}
                  <div className="spell-type-filters">
                    <button
                      className={`spell-type-tab ${spellTypeFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setSpellTypeFilter('all')}
                    >All ({allSpells.length})</button>
                    {typeKeys.map(type => {
                      const meta = SPELL_TYPE_META[type] || { label: type, color: '#888', icon: '✨' };
                      return (
                        <button
                          key={type}
                          className={`spell-type-tab ${spellTypeFilter === type ? 'active' : ''}`}
                          onClick={() => setSpellTypeFilter(type)}
                          style={{ '--tab-color': meta.color }}
                        >{meta.icon} {meta.label} ({spellTypes[type].length})</button>
                      );
                    })}
                  </div>

                  {/* Search + bulk actions for current view */}
                  <div className="spell-filter-bar">
                    <input
                      className="inv-search"
                      type="text"
                      placeholder="🔍 Search spells..."
                      value={spellSearch}
                      onChange={(e) => setSpellSearch(e.target.value)}
                    />
                    <button
                      className="inv-btn equip"
                      onClick={() => handleEquipAllVisible(visibleNames)}
                      disabled={!!busyAction}
                      title="Equip all visible spells"
                      style={{ fontSize: '0.65rem' }}
                    >+ All Visible</button>
                    <button
                      className="inv-btn drop"
                      onClick={() => handleUnequipAllVisible(visibleNames)}
                      disabled={!!busyAction}
                      title="Unequip all visible spells"
                      style={{ fontSize: '0.65rem' }}
                    >All Visible</button>
                  </div>

                  {/* Spell list */}
                  {visibleSpells.length === 0 ? (
                    <div className="inv-empty">No spells match your filters.</div>
                  ) : (
                    visibleSpells.map((spell, idx) => {
                      const meta = SPELL_TYPE_META[spell.type] || { label: spell.type, color: '#888', icon: '✨' };
                      const isEquipped = equippedSpellNames.includes(spell.name);
                      return (
                        <div
                          key={spell.name}
                          className={`spell-row ${isEquipped ? 'equipped' : ''}`}
                          onClick={() => handleToggleSpell(spell.name)}
                        >
                          <div className="spell-row-check">
                            {isEquipped ? '' : ''}
                          </div>
                          <div className="spell-row-color" style={{ backgroundColor: meta.color }} />
                          <div className="spell-row-info">
                            <div className="spell-row-name">
                              <span style={{ color: isEquipped ? meta.color : '#ccc' }}>{spell.name}</span>
                              <span className="spell-row-mp">{spell.mp} MP</span>
                            </div>
                            <div className="spell-row-meta">
                              <span className="spell-row-type-badge" style={{ background: meta.color }}>
                                {meta.icon} {meta.label}
                              </span>
                              {spell.element && (
                                <span className="spell-row-element">{spell.element}</span>
                              )}
                              {spell.prereqs && (
                                <span className="spell-row-prereqs">
                                  {Object.entries(spell.prereqs).map(([seed, lvl]) => `${seed} ${lvl}`).join(', ')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}

                  {/* Reminder to save */}
                  <div className="spell-save-reminder">
                    Remember to  <strong>Save Loadout</strong> when done. Only saved changes take effect in combat.
                  </div>
                </>
              );
            })()}
          </div>
        ) : (

        /* ITEM LIST */
        <div className="inv-list">
          {filteredItems.length === 0 ? (
            <div className="inv-empty">
              {searchText ? `No items matching "${searchText}"` : 'No items in this category.'}
            </div>
          ) : (
            filteredItems.map((item, idx) => {
              const cat = itemCategory(item.type);
              const canEquip = EQUIPPABLE_TYPES.includes(item.type);
              const canUse = USABLE_TYPES.includes(item.type) && !isEnchantScroll(item);
              const isTome = item.type === 'tome';
              const color = getRarityColor(item);
              const qty = item.quantity || 1;

              return (
                <div
                  key={`${item.name}-${idx}`}
                  className="inv-item"
                  onMouseEnter={!isTouchDevice ? (e) => handleMouseEnter(e, item) : undefined}
                  onMouseLeave={!isTouchDevice ? handleMouseLeave : undefined}
                  onClick={isTouchDevice ? (e) => handleItemTap(e, item) : undefined}
                >
                  {/* Left: color bar + info */}
                  <div className="inv-item-color-bar" style={{ backgroundColor: color }} />
                  <div className="inv-item-info">
                    <div className="inv-item-row1">
                      {item.locked && <span className="inv-item-lock" title="Locked — protected from sell/salvage/drop/list/mail" style={{ marginRight: 4 }}>🔒</span>}
                      <span className="inv-item-name" style={{ color }}>{item.name}</span>
                      {qty > 1 && <span className="inv-item-qty">x{qty}</span>}
                    </div>
                    <div className="inv-item-row2">
                      <span className="inv-item-type">{item.type}</span>
                      {canEquip && <span className="inv-item-slot">{getEquipSlotLabel(item)}</span>}
                      {item.description && item.description !== 'Unknown item' && <span className="inv-item-desc">{item.description}</span>}
                    </div>
                  </div>

                  {/* Right: action buttons */}
                  <div className="inv-item-actions">
                    {canEquip && (
                      <button
                        className="inv-btn equip"
                        onClick={() => handleEquip(item.name)}
                        disabled={!!busyAction}
                        title={`Equip to ${getEquipSlotLabel(item)}`}
                      >Equip</button>
                    )}
                    {item.type === 'weapon' && hasDualWield && (
                      <button
                        className="inv-btn equip"
                        onClick={() => handleEquip(item.name, 'offhand')}
                        disabled={!!busyAction}
                        title="Equip to Off-Hand"
                        style={{ fontSize: '0.75em' }}
                      >🗡️ Off-Hand</button>
                    )}
                    {item.type === 'ring' && (
                      <button
                        className="inv-btn equip"
                        onClick={() => handleEquip(item.name, 'ring_right')}
                        disabled={!!busyAction}
                        title="Equip to Ring (R)"
                        style={{ fontSize: '0.75em' }}
                      >💍 Ring (R)</button>
                    )}
                    {canUse && (
                      <button
                        className={`inv-btn ${item.type === 'recipe_scroll' ? 'tome' : 'use'}`}
                        onClick={() => handleUseItem(item.name)}
                        disabled={!!busyAction}
                        title={item.type === 'recipe_scroll' ? `Learn recipe from ${item.name}` : undefined}
                      >{item.type === 'recipe_scroll' ? 'Learn' : 'Use'}</button>
                    )}
                    {isEnchantScroll(item) && (
                      <span
                        className="inv-btn"
                        style={{ fontSize: '0.7em', opacity: 0.75, cursor: 'default', background: '#2a1a4c', color: '#c9a84c', border: '1px solid #9b59b6' }}
                        title="Apply at a crafting station — open the Crafting Bench and click the '📜 Scrolls' tab"
                      >📜 Scrolls Tab</span>
                    )}
                    {isTome && (
                      <button
                        className="inv-btn tome"
                        onClick={() => handleUseTome(item.name)}
                        disabled={!!busyAction || !canLearnSpells}
                        title={canLearnSpells ? `Study tome to learn ${item.spell || 'spell'}` : `${character?.class || 'Your class'} cannot learn magic`}
                      >{canLearnSpells ? 'Learn' : '🚫'}</button>
                    )}
                    <button
                      className="inv-btn drop"
                      onClick={() => {
                        setConfirmDrop(item);
                        setDropQty(qty > 1 ? qty : 1);
                      }}
                      disabled={!!busyAction || item.type === 'quest' || item.droppable === false || item.locked}
                      title={item.locked ? 'Locked — unlock (⭐) first' : (item.type === 'quest' || item.droppable === false ? 'Quest items cannot be dropped' : 'Drop item')}
                    ></button>
                    {category !== 'pouch' && (
                      <button
                        className="inv-btn"
                        onClick={() => handleToggleLock(item)}
                        disabled={!!busyAction}
                        title={item.locked ? 'Unlock — allow sell/salvage/drop/list/mail' : 'Lock — protect from sell/salvage/drop/list/mail'}
                        style={{
                          fontSize: '0.85em',
                          background: item.locked ? '#3a2a1a' : '#1a1a1a',
                          color: item.locked ? '#f1c40f' : '#888',
                          border: `1px solid ${item.locked ? '#f1c40f' : '#444'}`,
                          minWidth: 32
                        }}
                      >{item.locked ? '🔒' : '⭐'}</button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        )}

        {/* Gold display */}
        <div className="inv-footer">
          <span className="inv-gold">{formatCurrency(character.gold || 0)} gp</span>
          <span className="inv-footer-hint">I or Esc to close</span>
        </div>

        {/* DROP CONFIRMATION MODAL */}
        {confirmDrop && (
          <div className="inv-drop-modal">
            <div className="inv-drop-content">
              <h4>Drop {confirmDrop.name}?</h4>
              {(confirmDrop.quantity || 1) > 1 ? (
                <>
                  <p>How many to drop? (have {confirmDrop.quantity})</p>
                  <div className="inv-drop-controls">
                    <button onClick={() => setDropQty(Math.max(1, dropQty - 1))}>-</button>
                    <input
                      type="number"
                      min="1"
                      max={confirmDrop.quantity || 1}
                      value={dropQty}
                      onChange={(e) => setDropQty(Math.max(1, Math.min(confirmDrop.quantity || 1, parseInt(e.target.value) || 1)))}
                    />
                    <button onClick={() => setDropQty(Math.min(confirmDrop.quantity || 1, dropQty + 1))}>+</button>
                    <button onClick={() => setDropQty(confirmDrop.quantity || 1)}>All</button>
                  </div>
                </>
              ) : (
                <p>Are you sure? This item will be lost forever.</p>
              )}
              <div className="inv-drop-buttons">
                <button className="inv-btn drop" onClick={() => handleDropItem(confirmDrop.name, dropQty)}>
                  Drop {dropQty > 1 ? `${dropQty}x ` : ''}{confirmDrop.name}
                </button>
                <button className="inv-btn cancel" onClick={() => setConfirmDrop(null)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* UNSAVED LOADOUT PROMPT */}
        {unsavedPrompt && (
          <div className="inv-drop-modal">
            <div className="inv-drop-content">
              <h4>💾 Save your spell loadout?</h4>
              <p>You have unsaved changes. Only saved loadouts take effect in combat.</p>
              <div className="inv-drop-buttons" style={{ flexWrap: 'wrap' }}>
                <button
                  className="inv-btn spell-save"
                  onClick={handleConfirmSaveAndProceed}
                  disabled={busyAction === 'saving-spells'}
                >💾 Save & Continue</button>
                <button
                  className="inv-btn drop"
                  onClick={handleConfirmDiscardAndProceed}
                  disabled={busyAction === 'saving-spells'}
                >Discard Changes</button>
                <button
                  className="inv-btn cancel"
                  onClick={() => setUnsavedPrompt(null)}
                  disabled={busyAction === 'saving-spells'}
                >Keep Editing</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* TOOLTIP - rendered outside inv-panel to avoid overflow:hidden clipping */}
      {tooltipItem && (
        <ItemTooltip
          ref={tooltipRef}
          item={tooltipItem}
          equippedItem={tooltipEquipped}
          canLearnSpells={canLearnSpells}
          onClose={() => { setTooltipItem(null); setTooltipEquipped(null); }}
          style={{
            position: 'fixed',
            left: `${tooltipPos.x}px`,
            top: `${tooltipPos.y}px`,
            zIndex: 2000,
          }}
        />
      )}
    </div>
  );
};

export default InventoryPanel;
